import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, Flame, Target, TrendingUp, Calendar,
  Dumbbell, BarChart2, ChevronDown, ChevronUp,
  Zap,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Cell,
} from "recharts";
import BottomNav from "../components/ui/BottomNav";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

function KpiCard({ icon, value, label, sub, color }) {
  return (
    <div className="flex-1 rounded-2xl border border-white/8 p-4" style={{ background: "#111" }}>
      <div className="flex items-center justify-between mb-2">
        <span style={{ color }}>{icon}</span>
        {sub && <span className="text-[10px] text-zinc-600 font-semibold">{sub}</span>}
      </div>
      <p className="text-3xl font-black text-white tabular-nums leading-none">{value}</p>
      <p className="text-zinc-500 text-xs font-semibold mt-1 uppercase tracking-wide">{label}</p>
    </div>
  );
}

function OefeningKaart({ oef, view }) {
  const [open, setOpen] = useState(false);
  const progressie = oef.progressie || [];
  const korteDatum = (d) => {
    const parts = d.split("-");
    return `${parts[2]}/${parts[1]}`;
  };

  return (
    <div className="rounded-2xl border border-white/8 overflow-hidden" style={{ background: "#111" }}>
      <button className="w-full px-4 py-3.5 flex items-center gap-3 text-left"
        onClick={() => setOpen(o => !o)}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "rgba(201,168,76,0.12)" }}>
          <Dumbbell size={15} style={{ color: GOLD }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-sm truncate">{oef.naam}</p>
          {view === "kracht" ? (
            <p className="text-zinc-500 text-xs mt-0.5">
              Beste 1RM: <span className="font-bold" style={{ color: GOLD }}>{oef.beste_1rm} kg</span>
            </p>
          ) : (
            <p className="text-zinc-500 text-xs mt-0.5">
              {progressie.length} sessie{progressie.length !== 1 ? "s" : ""} gelogd
            </p>
          )}
        </div>
        {open ? <ChevronUp size={14} className="text-zinc-600 shrink-0" /> : <ChevronDown size={14} className="text-zinc-600 shrink-0" />}
      </button>

      {open && (
        <div className="border-t border-white/5 px-4 pb-4 pt-3">
          {view === "kracht" && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-black/40 rounded-xl p-3 text-center">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wide mb-1">Geschatte 1RM</p>
                <p className="text-xl font-black" style={{ color: GOLD }}>{oef.geschatte_1rm} kg</p>
                <p className="text-[10px] text-zinc-600 mt-0.5">huidig</p>
              </div>
              <div className="bg-black/40 rounded-xl p-3 text-center">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wide mb-1">Beste 1RM</p>
                <p className="text-xl font-black text-white">{oef.beste_1rm} kg</p>
                <p className="text-[10px] text-zinc-600 mt-0.5">{oef.beste_1rm_datum ? korteDatum(oef.beste_1rm_datum) : "–"}</p>
              </div>
            </div>
          )}

          {progressie.length >= 2 && (
            <div style={{ height: 120 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={progressie} margin={{ top: 4, right: 4, left: -30, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="datum" tickFormatter={korteDatum}
                    tick={{ fill: "#52525b", fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#52525b", fontSize: 9 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: "#111", border: `1px solid ${GOLD}33`, borderRadius: 8, fontSize: 11 }}
                    labelStyle={{ color: "#a1a1aa" }}
                    itemStyle={{ color: GOLD }}
                    formatter={(v) => [`${v} kg`, view === "kracht" ? "Est. 1RM" : "Gewicht"]}
                    labelFormatter={korteDatum}
                  />
                  <Line
                    type="monotone"
                    dataKey={view === "kracht" ? "est_1rm" : "gewicht"}
                    stroke={GOLD}
                    strokeWidth={2}
                    dot={{ fill: GOLD, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {progressie.length < 2 && (
            <p className="text-zinc-600 text-xs text-center py-4">Log minstens 2 sessies voor een grafiek</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProgressDashboard() {
  const { user } = useAuth();
  const token = user?.token;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kracht"); // kracht | consistentie

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/workout/progress-dashboard`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const korteDatum = (d) => {
    if (!d) return "";
    const parts = d.split("-");
    return `W${parts[2].slice(-2)}/${parts[1]}`;
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: "#080808", color: "white" }}>
      {/* Header */}
      <div className="sticky top-0 z-30 px-4 pt-12 pb-4 flex items-center gap-3"
        style={{ background: "#080808", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <button onClick={() => navigate("/account")}
          className="w-8 h-8 flex items-center justify-center rounded-xl text-zinc-400 hover:text-white transition-colors"
          data-testid="progress-terug">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-base font-black text-white flex-1">Progress Dashboard</h1>
        {data && (
          <span className="flex items-center gap-1 text-[11px] font-black px-2.5 py-1 rounded-full"
            style={{ background: "rgba(201,168,76,0.15)", color: GOLD }}
            data-testid="streak-badge">
            <Flame size={11} /> {data.streak_dagen}d streak
          </span>
        )}
      </div>

      <div className="px-4 pt-5 space-y-5">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 rounded-2xl animate-pulse" style={{ background: "#111" }} />
            ))}
          </div>
        ) : !data ? (
          <div className="text-center py-20 text-zinc-600">
            <Dumbbell size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">Log eerst een training om je voortgang te zien</p>
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="flex gap-3" data-testid="kpi-cards">
              <KpiCard
                icon={<Flame size={18} />}
                value={data.streak_dagen}
                label="Streak"
                sub={`Record: ${data.record_streak}d`}
                color="#f59e0b"
              />
              <KpiCard
                icon={<Target size={18} />}
                value={data.totaal_sessies}
                label="Totaal sessies"
                color={GOLD}
              />
            </div>

            {/* Filter knoppen */}
            <div className="flex gap-2">
              <button
                onClick={() => setView("kracht")}
                data-testid="filter-kracht"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all"
                style={{
                  background: view === "kracht" ? GOLD : "transparent",
                  color: view === "kracht" ? "#000" : GOLD,
                  border: `1.5px solid ${GOLD}`,
                }}
              >
                <TrendingUp size={13} /> Kracht
              </button>
              <button
                onClick={() => setView("consistentie")}
                data-testid="filter-consistentie"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all"
                style={{
                  background: view === "consistentie" ? GOLD : "transparent",
                  color: view === "consistentie" ? "#000" : GOLD,
                  border: `1.5px solid ${GOLD}`,
                }}
              >
                <Calendar size={13} /> Consistentie
              </button>
            </div>

            {/* Consistentie grafiek */}
            {view === "consistentie" && (
              <div className="rounded-2xl border border-white/8 p-4" style={{ background: "#111" }}>
                <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-4">
                  Trainingen per week
                </p>
                {data.consistentie.length > 0 ? (
                  <div style={{ height: 140 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.consistentie} margin={{ top: 0, right: 0, left: -30, bottom: 0 }}>
                        <XAxis dataKey="week" tickFormatter={korteDatum}
                          tick={{ fill: "#52525b", fontSize: 9 }} axisLine={false} tickLine={false} />
                        <YAxis allowDecimals={false}
                          tick={{ fill: "#52525b", fontSize: 9 }} axisLine={false} tickLine={false} />
                        <Tooltip
                          cursor={{ fill: "rgba(201,168,76,0.05)" }}
                          contentStyle={{ background: "#111", border: `1px solid ${GOLD}33`, borderRadius: 8, fontSize: 11 }}
                          labelStyle={{ color: "#a1a1aa" }}
                          itemStyle={{ color: GOLD }}
                          formatter={(v) => [`${v}×`, "trainingen"]}
                          labelFormatter={korteDatum}
                        />
                        <Bar dataKey="trainingen" radius={[4, 4, 0, 0]}>
                          {data.consistentie.map((_, i) => (
                            <Cell key={i}
                              fill={i === data.consistentie.length - 1 ? GOLD : "rgba(201,168,76,0.25)"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-zinc-600 text-sm text-center py-6">Nog geen trainingen gelogd</p>
                )}
              </div>
            )}

            {/* Oefeningen */}
            {view === "kracht" && (
              <>
                <p className="text-zinc-600 text-[10px] font-black uppercase tracking-widest">
                  Oefening
                </p>
                {data.oefeningen.length === 0 ? (
                  <div className="text-center py-8 text-zinc-600 text-sm">
                    <Zap size={28} className="mx-auto mb-2 opacity-30" />
                    Log een training met gewichten om je kracht te tracken
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="oefeningen-lijst">
                    {data.oefeningen.map((oef, i) => (
                      <OefeningKaart key={i} oef={oef} view={view} />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
